import { useEffect } from 'react';
import type { ProStudioNodeDataStore } from '@pypestream/pro-studio-collab-client';

import {
  startCollabTableFeed,
  startPresenceShare,
  type CollabFeedTarget,
} from './collab-table-feed';

export type CollabTableFeedParams = {
  /** The draft's collab store; null for a legacy draft or before it connects. */
  store: ProStudioNodeDataStore | null;
  /** The draft the store was opened for. */
  solutionVersionId: string | undefined;
  /** The draft the table store is on (`tableStore.solutionVersionId`). */
  tableSolutionVersionId: string;
  /** `tableStore.versionRevisitRequests`: every new visit re-shows the loader. */
  visit: number;
  table: CollabFeedTarget;
};

/**
 * Feeds the Workflows table from the draft's collab store (PSTUDIO-4450).
 *
 * Waits until the table store is on the same draft, because its load path
 * refuses a payload for any other version. Starts over on every new visit:
 * a visit puts the table back on its loader, and only a fresh load takes it
 * off again.
 */
export const useCollabTableFeed = ({
  store,
  solutionVersionId,
  tableSolutionVersionId,
  visit,
  table,
}: CollabTableFeedParams): void => {
  const tableOnDraft =
    !!solutionVersionId && tableSolutionVersionId === solutionVersionId;

  useEffect(() => {
    if (!store || !solutionVersionId || !tableOnDraft) return undefined;
    return startCollabTableFeed(store, solutionVersionId, table);
  }, [store, solutionVersionId, tableOnDraft, visit, table]);
};

/**
 * Shares this user's selected cell with the draft's coworkers while `enabled`
 * (the Workflows page). Its own effect, so turning it off or on never restarts
 * the table feed (PSTUDIO-4449).
 */
export const useCollabPresenceShare = ({
  store,
  table,
  enabled,
}: {
  store: ProStudioNodeDataStore | null;
  table: Pick<CollabFeedTarget, 'selectedStartCell' | 'selectedCells'>;
  enabled: boolean;
}): void => {
  useEffect(() => {
    if (!store || !enabled) return undefined;
    return startPresenceShare(store, table);
  }, [store, table, enabled]);
};
